import { z } from 'zod';

/* ------------------------------------------------------------------ */
/* Realms: who is signing in                                           */
/* ------------------------------------------------------------------ */

/**
 * Every person who signs in belongs to exactly one realm. A phone number can exist once
 * per realm (the same person may be a customer and a rider, as two separate identities).
 */
export const REALMS = [
  'customer',
  'partner',
  'rider',
  'staff',
  'mi',
  'mi_lead',
  'support',
] as const;
export const RealmSchema = z.enum(REALMS);
export type Realm = z.infer<typeof RealmSchema>;

/** Realms whose people sign up on their own. Everyone else must be invited first. */
export const SELF_SIGNUP_REALMS: readonly Realm[] = ['customer', 'rider'];

/** Realms bound to one device: a new device needs a fresh OTP and logs out the old one. */
export const DEVICE_BOUND_REALMS: readonly Realm[] = ['partner', 'rider', 'mi', 'support'];

/* ------------------------------------------------------------------ */
/* Phone numbers (India + Nepal only)                                  */
/* ------------------------------------------------------------------ */

export const PHONE_COUNTRIES = {
  IN: { dial: '+91', digits: 10, pattern: /^[6-9]\d{9}$/ },
  NP: { dial: '+977', digits: 10, pattern: /^9[678]\d{8}$/ },
} as const;
export type PhoneCountry = keyof typeof PHONE_COUNTRIES;

/** E.164 for India (+91 6–9xxxxxxxxx) or Nepal (+977 96/97/98xxxxxxxx). */
export const PhoneSchema = z
  .string()
  .trim()
  .regex(/^\+(91[6-9]\d{9}|9779[678]\d{8})$/, 'Enter a valid Indian or Nepali mobile number');
export type Phone = z.infer<typeof PhoneSchema>;

export function phoneCountry(phone: string): PhoneCountry {
  return phone.startsWith('+977') ? 'NP' : 'IN';
}

/** Shows only the last three digits: +91 98•••••210. */
export function maskPhone(phone: string): string {
  const country = phoneCountry(phone);
  const dial = PHONE_COUNTRIES[country].dial;
  const local = phone.slice(dial.length);
  return `${dial} ${local.slice(0, 2)}${'•'.repeat(Math.max(0, local.length - 5))}${local.slice(-3)}`;
}

/* ------------------------------------------------------------------ */
/* Permissions and roles                                               */
/* ------------------------------------------------------------------ */

export const PERMISSIONS = [
  'geo.view',
  'geo.manage',
  'planning.view',
  'planning.manage',
  'staff.view',
  'staff.manage',
  'otp.reveal',
  'audit.view',
  'config.view',
  'config.manage',
  'restaurants.view',
  'restaurants.onboard',
  'restaurants.review',
  'agreements.manage',
  'menu.view',
  'menu.edit',
  'menu.review',
  'subscriptions.view',
  'subscriptions.manage',
  'plans.manage',
  'orders.view',
  'orders.manage',
  'discovery.view',
  'discovery.manage',
  'offers.view',
  'offers.manage',
  'payments.view',
  'payments.manage',
  'payouts.manage',
  'riders.view',
  'riders.manage',
  'dispatch.manage',
  'tower.view',
  'tower.act',
  'tower.manage',
  'support.work',
  'support.lead',
  'support.admin',
  'trust.view',
  'trust.manage',
  'growth.view',
  'growth.manage',
  'notify.manage',
  'insights.view',
  'insights.manage',
  'mi.view',
  'mi.manage',
  'legal.view',
  'legal.manage',
  'launch.view',
  'launch.manage',
  'security.view',
  'security.manage',
  'pii.reveal',
] as const;
export const PermissionSchema = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof PermissionSchema>;

export const ROLE_KEYS = [
  'owner',
  'admin',
  'city_planner',
  'ops_viewer',
  'fleet_captain',
  'support_lead',
  'support_agent',
  'mi_lead',
  'mi',
  'partner_owner',
  'partner_staff',
  'rider',
  'customer',
] as const;
export const RoleKeySchema = z.enum(ROLE_KEYS);
export type RoleKey = z.infer<typeof RoleKeySchema>;

/** Roles a staff member may hold in Capsico OS. */
export const STAFF_ROLE_KEYS = [
  'owner',
  'admin',
  'city_planner',
  'ops_viewer',
  'fleet_captain',
  'mi_lead',
] as const;
export const StaffRoleKeySchema = z.enum(STAFF_ROLE_KEYS);

/** A staff member may hold any number of roles (up to every staff role); duplicates are dropped. */
export const StaffRolesSchema = z
  .array(StaffRoleKeySchema)
  .min(1)
  .max(STAFF_ROLE_KEYS.length)
  .transform((roles) => [...new Set(roles)]);

/* ------------------------------------------------------------------ */
/* One-time codes                                                       */
/* ------------------------------------------------------------------ */

export const OTP_LENGTH = 6;
export const OtpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code');

export const TotpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app');

/** A browser or app install, generated once and kept on the device. Not a secret. */
export const DeviceIdSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{16,64}$/, 'Invalid device id');

export const OtpStartInputSchema = z.object({ phone: PhoneSchema });
export type OtpStartInput = z.infer<typeof OtpStartInputSchema>;

export const OtpStartResultSchema = z.object({
  /** Masked number the code was sent to. */
  sentTo: z.string(),
  /** Seconds before the user may ask for another code. */
  resendInSeconds: z.number().int().min(0),
  expiresInSeconds: z.number().int().min(1),
  /** True when SMS failed: the user should ask support for the code. */
  smsDelayed: z.boolean(),
});
export type OtpStartResult = z.infer<typeof OtpStartResultSchema>;

export const OtpVerifyInputSchema = z.object({
  phone: PhoneSchema,
  code: OtpCodeSchema,
  name: z.string().trim().min(1).max(80).optional(),
});
export type OtpVerifyInput = z.infer<typeof OtpVerifyInputSchema>;

/* ------------------------------------------------------------------ */
/* Staff sign-in: phone OTP, then authenticator app                    */
/* ------------------------------------------------------------------ */

export const StaffOtpVerifyResultSchema = z.object({
  /** Short-lived ticket for the authenticator step. Never stored by the client. */
  ticket: z.string(),
  next: z.enum(['totp_enrol', 'totp_verify']),
  ticketExpiresInSeconds: z.number().int().min(1),
});
export type StaffOtpVerifyResult = z.infer<typeof StaffOtpVerifyResultSchema>;

export const StaffTicketInputSchema = z.object({ ticket: z.string().min(20).max(200) });

export const StaffTotpEnrolResultSchema = z.object({
  /** Base32 secret, shown once for manual entry. */
  secret: z.string(),
  /** otpauth:// link for the QR code. */
  otpauthUri: z.string(),
  issuer: z.string(),
  accountLabel: z.string(),
});
export type StaffTotpEnrolResult = z.infer<typeof StaffTotpEnrolResultSchema>;

export const StaffTotpInputSchema = z.object({
  ticket: z.string().min(20).max(200),
  code: TotpCodeSchema,
});
export type StaffTotpInput = z.infer<typeof StaffTotpInputSchema>;

/* ------------------------------------------------------------------ */
/* Session                                                              */
/* ------------------------------------------------------------------ */

export const MeSchema = z.object({
  identityId: z.uuid(),
  realm: RealmSchema,
  name: z.string().nullable(),
  phoneMasked: z.string(),
  roles: z.array(RoleKeySchema),
  permissions: z.array(PermissionSchema),
  sessionExpiresAt: z.string(),
});
export type Me = z.infer<typeof MeSchema>;

/** Returned to apps (not browsers) so they can keep the token in secure storage. */
export const AppSessionSchema = z.object({ token: z.string(), me: MeSchema });
export type AppSession = z.infer<typeof AppSessionSchema>;

/** Header apps send so the API returns a token instead of setting a cookie. */
export const CLIENT_HEADER = 'x-capsico-client';
export const DEVICE_HEADER = 'x-device-id';
export const SESSION_COOKIE = 'sid';

/* ------------------------------------------------------------------ */
/* Staff admin                                                          */
/* ------------------------------------------------------------------ */

export const IDENTITY_STATUSES = ['invited', 'active', 'suspended'] as const;
export const IdentityStatusSchema = z.enum(IDENTITY_STATUSES);
export type IdentityStatus = z.infer<typeof IdentityStatusSchema>;

export const StaffMemberSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  phoneMasked: z.string(),
  status: IdentityStatusSchema,
  roles: z.array(RoleKeySchema),
  authenticatorSet: z.boolean(),
  lastSignInAt: z.string().nullable(),
  createdAt: z.string(),
  version: z.number().int(),
});
export type StaffMember = z.infer<typeof StaffMemberSchema>;

export const InviteStaffInputSchema = z.object({
  name: z.string().trim().min(2).max(80),
  phone: PhoneSchema,
  roles: StaffRolesSchema,
});
export type InviteStaffInput = z.infer<typeof InviteStaffInputSchema>;

export const UpdateStaffInputSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    roles: StaffRolesSchema.optional(),
    status: z.enum(['active', 'suspended']).optional(),
  })
  .refine((value) => value.roles !== undefined || value.status !== undefined, {
    message: 'Change the roles or the status',
  });
export type UpdateStaffInput = z.infer<typeof UpdateStaffInputSchema>;

export const ResetAuthenticatorInputSchema = z.object({ expectedVersion: z.number().int().min(1) });

/* ------------------------------------------------------------------ */
/* OTP vault (support)                                                  */
/* ------------------------------------------------------------------ */

export const OtpDeliverySchema = z.object({
  id: z.uuid(),
  realm: RealmSchema,
  phoneMasked: z.string(),
  purpose: z.string(),
  provider: z.string(),
  status: z.enum(['sent', 'failed', 'consumed', 'expired']),
  createdAt: z.string(),
  expiresAt: z.string(),
  /** Only when SMS failed and the code is still valid: support may reveal it. */
  revealable: z.boolean(),
});
export type OtpDelivery = z.infer<typeof OtpDeliverySchema>;

export const AuthAuditEntrySchema = z.object({
  id: z.number().int(),
  identityId: z.uuid().nullable(),
  realm: RealmSchema.nullable(),
  event: z.string(),
  phoneMasked: z.string().nullable(),
  ip: z.string().nullable(),
  at: z.string(),
  detail: z.record(z.string(), z.unknown()),
});
export type AuthAuditEntry = z.infer<typeof AuthAuditEntrySchema>;
